import { Typography, withStyles, WithStyles } from '@material-ui/core'
import classNames from 'classnames'
import moment from 'moment'
import { FC, Fragment } from 'react'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { ApplicationStatus, LeadStatuses } from '../../../types/applications'
import { getApplicationStatus } from '../values/handleApplicationsData'
import { ApplicationCardProps } from './ApplicationCard'
import applicationCardStyles from './styles'
import { CustomTooltip } from '../../../components/Display/Helpers'

interface ApplicationCardContent extends ApplicationCardProps, WithStyles {
  createdAtString: string
  name: string
}

const ApplicationCardContent: FC<ApplicationCardContent> = ({
  classes,
  application,
  t,
  createdAtString,
  showings,
  name,
  width
}) => {
  const {
    data,
    message,
    applicantMessage,
    status,
    leadId,
    partnerLead
  } = application
  const {
    age,
    creditStatus,
    lifeSituation,
    lifeSituationCustom,
    pets,
    movingWith,
    phone
  } = data
  const notePreviewText = typeof data.notes === 'string' ? data.notes.trim() : ''
  const showNotesPreview = !lifeSituation && notePreviewText.length > 0
  const shouldShowNotePreviewFade = showNotesPreview && notePreviewText.length > 140
  const applicationStatus = getApplicationStatus(application)
  const incompleteProfile =
    leadId &&
    !lifeSituation
  // partnerLead &&
  // partnerLead.status !== LeadStatuses.profileCompleted
  const tag =
    applicationStatus === 'showingReserved'
      ? (showings?.length &&
        showings?.filter((showing) => {
          return showing.applicationIds?.includes(application.applicationId)
        })
          .map((showing) => {
            const date = moment(showing.startDate).format('D.M,')
            const startTime = moment(showing.startDate).format('HH:mm')
            return (
              <div key={showing.showingId} className={classes.showingsPopupItem}>
                <Typography variant='body1'>
                  {`${t('showings.startTime')}: ${date}`}{'\u00A0'}
                </Typography>
                <Typography variant='body1'>
                  {`${t('showings.startTimeClock')} ${startTime}`}
                </Typography>
              </div>
            )
          })) || null
      : applicationStatus === 'created'
        ? (incompleteProfile
          ? t('applicantCard.label.inquiry')
          : t('applicantCard.label.newApplication'))
        : t(`application.status.${getApplicationStatus(application)}`);

  const emptyListItem = () => (
    <li className={classes.listItem}>
      <Typography
        className={classes.listItemText}
        variant={width === 'xs' ? 'body1' : 'inherit'}
      >
        {/* XXX-XXX */}
      </Typography>
    </li>
  )

  return (
    <Fragment>
      {tag && (
        <span
          className={classNames({
            [classes.status]: true,
            [classes.statusManual]: status === ApplicationStatus.manual
          })}
        >
          {tag}
        </span>
      )}
      <section className={classes.cardContent}>
        <Typography
          variant={width === 'xs' ? 'h6' : 'h5'}
          className={classes.cardApplicantName}
          style={{ maxWidth: '70px' }}
        >
          {name}
        </Typography>
        <Typography variant='body2' className={classes.createdDate}>
          {createdAtString}
        </Typography>
        <ul style={{ fontSize: '18px' }} className={classNames({ [classes.list]: true })}>
          {showNotesPreview ? (
            <>
              <li className={classNames(classes.listItem, classes.notePreviewRow)}>
                <div className={classes.notePreviewContainer}>
                  <Typography
                    className={classNames(classes.listItemText, classes.notePreviewText)}
                    variant={width === 'xs' ? 'body1' : 'inherit'}
                  >
                    {notePreviewText}
                  </Typography>
                  {shouldShowNotePreviewFade && (
                    <span className={classes.notePreviewGradient} aria-hidden='true' />
                  )}
                </div>
              </li>
            </>
          ) : (
            <>
              {age && age !== 'noAnswer' && age !== 'error' ? (
                <li className={classes.listItem}>
                  <Typography
                    className={classes.listItemText}
                    variant={width === 'xs' ? 'body1' : 'inherit'}
                  >
                    {`${t('application.list.age')}: ${t(`application.age.${age}`)}`}
                  </Typography>
                </li>
              ) : (
                emptyListItem()
              )}
              {lifeSituation &&
                lifeSituation !== 'other' &&
                lifeSituation !== 'error' ? (
                <li className={classes.listItem}>
                  <Typography
                    className={classes.listItemText}
                    variant={width === 'xs' ? 'body1' : 'inherit'}
                  >
                    {lifeSituation.toString() === 'myAnswer' ? (
                      <>{lifeSituationCustom}</>
                    ) : (
                      <>{t(`application.lifeSituation.${lifeSituation}`)}</>
                    )}
                  </Typography>
                </li>
              ) : (
                emptyListItem()
              )}
              {pets && pets !== 'error' ? (
                <li className={classes.listItem}>
                  <Typography
                    className={classes.listItemText}
                    variant={width === 'xs' ? 'body1' : 'inherit'}
                  >
                    {`${t('application.list.pets')}: ${t(`application.pets.${pets}`)}`}
                  </Typography>
                </li>
              ) : (
                emptyListItem()
              )}
              {movingWith && movingWith !== 'error' ? (
                <li className={classes.listItem}>
                  <Typography
                    className={classes.listItemText}
                    variant={width === 'xs' ? 'body1' : 'inherit'}
                  >
                    {`${t('application.list.movingWith')} ${t(`application.movingWith.${movingWith}`)}`}
                  </Typography>
                </li>
              ) : (
                emptyListItem()
              )}
              {creditStatus && creditStatus !== 'error' ? (
                <li className={classes.listItem}>
                  <div className={classes.creditStatusPreviewRow}>
                    <Typography
                      className={classes.listItemText}
                      variant={width === 'xs' ? 'body1' : 'inherit'}
                    >
                      {`${t('application.list.creditStatus')} ${t(`application.creditStatus.${creditStatus}`)}`}
                    </Typography>
                    <CustomTooltip
                      placement='top'
                      title={t('tooltips.applications.creditStatusLine0')}
                      clickToToggle
                    >
                      <InfoOutlinedIcon className={classes.creditStatusPreviewIcon} />
                    </CustomTooltip>
                  </div>
                </li>
              ) : (
                emptyListItem()
              )}
            </>
          )}
        </ul>
      </section>

      {message && status === ApplicationStatus.manual && (
        <Typography className={classes.message}>{message}</Typography>
      )}
      {applicantMessage &&
        (status === ApplicationStatus.noAvailableTimes ||
          status === ApplicationStatus.notInterested) && (
          <Typography className={classes.message}>
            {applicantMessage}
          </Typography>
        )}
    </Fragment>
  )
}

export default withStyles(applicationCardStyles)(ApplicationCardContent)
